import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemeType, ThemeConfig, ThemeContextType, ThemeProviderProps } from './types';
import { themes } from './themeConfigs';
import {
  getStoredTheme,
  setStoredTheme,
  applyThemeToDOM,
  initializeSystemThemeListener
} from './themeUtils';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light'
}) => {
  const [theme, setThemeState] = useState<ThemeType>(() => {
    return getStoredTheme() || defaultTheme;
  });

  const themeConfig: ThemeConfig = themes[theme];

  const setTheme = (newTheme: ThemeType) => {
    setThemeState(newTheme);
    setStoredTheme(newTheme);
    applyThemeToDOM(themes[newTheme]);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  useEffect(() => {
    applyThemeToDOM(themeConfig);

    const cleanup = initializeSystemThemeListener((systemTheme) => {
      setTheme(systemTheme);
    });

    return cleanup;
  }, []);

  useEffect(() => {
    applyThemeToDOM(themeConfig);

    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme, themeConfig]);

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
    themeConfig,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
